<!-- src/views/pages/superadmin/purchase/purchase-transaction.vue -->
<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, onMounted, computed } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { usePurchaseTransactionStore } from "@/stores/purchaseTransactionStore";
import svc from "@/services/super-purchase.service";
import { toast } from "@/utils/toast";
import PurchaseTable from "./purchase-table.vue";
import PurchaseModal from "@/components/modal/purchase-modal.vue";

const title = "Purchase Transaction";
const text = "Superadmin";
const text1 = "Purchase Transaction List";

const store = usePurchaseTransactionStore();
const dateRangeInput = ref(null);
const selectedTransaction = ref(null);

async function refreshTransactions() {
  await store.fetchTransactions({ page: 1 });
}
const paymentStatusLabel = computed(() => {
  const value = store.filters.payment_status;
  if (!value) return "All Statuses";
  if (value === "paid") return "Paid";
  if (value === "pending") return "Pending";
  if (value === "unpaid") return "Unpaid";
  if (value === "refunded") return "Refunded";
  if (value === "failed") return "Failed";
  return value;
});
const paymentMethodLabel = computed(() => {
  return store.filters.payment_method || "All Methods";
});

function formatCurrency(value) {
  const amount = Number(value || 0);
  return `KES ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function applySearch() {
  store.fetchTransactions({ page: 1 });
}

function clearFilters() {
  store.filters.payment_method = "";
  store.filters.payment_status = "";
  store.filters.q = "";
  const start = moment().subtract(6, "days");
  const end = moment();
  booking_range(start, end);
}

function toggleHeader() {
  document.getElementById("collapse-header").classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function booking_range(start, end) {
  store.filters.from = start.format("YYYY-MM-DD");
  store.filters.to = end.format("YYYY-MM-DD");
  store.fetchTransactions({ page: 1 });
  return start.format("M/D/YYYY") + " - " + end.format("M/D/YYYY");
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function exportTransactions(format) {
  try {
    const res = await svc.export(
      {
        from: store.filters.from || undefined,
        to: store.filters.to || undefined,
        payment_status: store.filters.payment_status || undefined,
        payment_method: store.filters.payment_method || undefined,
        q: store.filters.q || undefined,
      },
      format
    );

    downloadBlob(
      res.data,
      format === "pdf"
        ? "purchase-transactions-export.pdf"
        : "purchase-transactions-export.xlsx"
    );
  } catch (error) {
    toast(
      error?.response?.data?.message || "Failed to export transactions",
      "error"
    );
  }
}

onMounted(() => {
  const start = moment().subtract(6, "days");
  const end = moment();

  store.filters.from = start.format("YYYY-MM-DD");
  store.filters.to = end.format("YYYY-MM-DD");

  store.fetchTransactions();

  if (dateRangeInput.value) {
    new DateRangePicker(
      dateRangeInput.value,
      {
        startDate: start,
        endDate: end,
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        },
      },
      booking_range
    );
  }
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper dq-platform">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportTransactions('pdf')"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportTransactions('excel')"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row dq-stat-grid dq-cmd-strip">
        <div class="col-xl-3 col-sm-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="badge bg-outline-primary">Gross Billed</span>
                <i class="ti ti-receipt-2 text-primary"></i>
              </div>
              <h4 class="mb-1">{{ formatCurrency(store.summary.gross_billed) }}</h4>
              <p class="text-muted mb-0">All transaction amounts in current filter.</p>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-sm-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="badge bg-success">Paid</span>
                <i class="ti ti-cash text-success"></i>
              </div>
              <h4 class="mb-1">{{ formatCurrency(store.summary.paid_total) }}</h4>
              <p class="text-muted mb-0">Collected revenue.</p>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-sm-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="badge bg-warning">Pending</span>
                <i class="ti ti-clock-hour-4 text-warning"></i>
              </div>
              <h4 class="mb-1">{{ formatCurrency(store.summary.pending_total) }}</h4>
              <p class="text-muted mb-0">Pending or unpaid collections.</p>
            </div>
          </div>
        </div>
        <div class="col-xl-3 col-sm-6 d-flex">
          <div class="card flex-fill dq-cmd-tile">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="badge bg-danger">Refunded</span>
                <i class="ti ti-rotate-2 text-danger"></i>
              </div>
              <h4 class="mb-1">{{ formatCurrency(store.summary.refunded_total) }}</h4>
              <p class="text-muted mb-0">Returned collections.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Transaction List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <div class="me-3">
              <div class="input-group">
                <input
                  v-model.trim="store.filters.q"
                  type="text"
                  class="form-control"
                  placeholder="Search invoice, company, or email"
                  @keyup.enter="applySearch"
                />
                <button class="btn btn-primary" type="button" @click="applySearch">
                  Search
                </button>
              </div>
            </div>

            <!-- Payment Method filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ paymentMethodLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_method = 'Credit Card';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Credit Card
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_method = 'Mpesa';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Mpesa
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_method = '';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    All
                  </a>
                </li>
              </ul>
            </div>

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ paymentStatusLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_status = 'pending';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Pending
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_status = 'paid';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Paid
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_status = 'unpaid';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Unpaid
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_status = 'refunded';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Refunded
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_status = 'failed';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    Failed
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click="
                      store.filters.payment_status = '';
                      store.fetchTransactions({ page: 1 });
                    "
                  >
                    All
                  </a>
                </li>
              </ul>
            </div>

            <button class="btn btn-white" type="button" @click="clearFilters">
              Clear
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <PurchaseTable @view="selectedTransaction = $event" />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <!-- View Invoice / Delete Modals (still static for now) -->
  <PurchaseModal :transaction="selectedTransaction" @reviewed="selectedTransaction = $event; refreshTransactions()" />
</template>


